import { useMemo, useState } from 'react';
import { Button, Card, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SubtleBadge from '@/components/common/SubtleBadge';

type McpConnectSnippetProps = {
  httpUrl: string;
  intlHttpUrl?: string;
  embedded?: boolean;
};

type ConnectEndpoint = {
  id: string;
  title: string;
  region: string;
  regionBg: 'primary' | 'info';
  hint: string;
  url: string;
  showConfig: boolean;
};

function configJson(url: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        mizfatools: {
          url
        }
      }
    },
    null,
    2
  );
}

export function McpConnectSnippet({
  httpUrl,
  intlHttpUrl,
  embedded = false
}: McpConnectSnippetProps) {
  const [copied, setCopied] = useState<string | null>(null);

  const endpoints = useMemo(() => {
    const domestic: ConnectEndpoint = {
      id: 'domestic',
      title: 'Cursor، ChatGPT و Codex',
      region: 'سرور ایران',
      regionBg: 'primary',
      hint: 'آدرس را در تنظیمات MCP کلاینت وارد کنید. اگر فایل پیکربندی می‌خواهد، نمونه JSON را هم کپی کنید.',
      url: httpUrl,
      showConfig: true
    };
    const intl = intlHttpUrl?.trim();
    if (!intl || intl === httpUrl) return [domestic];
    return [
      domestic,
      {
        id: 'intl',
        title: 'Claude و Gemini',
        region: 'سرور خارج',
        regionBg: 'info',
        hint: 'اگر کلاینت از ایران به MCP نرسد، این آدرس را استفاده کنید.',
        url: intl,
        showConfig: false
      }
    ];
  }, [httpUrl, intlHttpUrl]);

  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      setCopied(null);
    }
  };

  const content = (
    <>
      <Card.Header as={embedded ? 'h6' : 'h5'} className="bg-body-tertiary mb-0">
        آدرس اتصال
      </Card.Header>
      <Card.Body>
        {!embedded && (
          <p className="text-600 fs--1 mb-3">
            هر کلاینت یک آدرس دارد. اول کلاینت را پیدا کنید، بعد همان آدرس را کپی
            کنید.
          </p>
        )}
        {endpoints.map((endpoint, index) => {
          const json = configJson(endpoint.url);
          const urlKey = `${endpoint.id}-url`;
          const jsonKey = `${endpoint.id}-json`;
          return (
            <div
              key={endpoint.id}
              className={`border rounded-3 p-3${index > 0 ? ' mt-3' : ''}`}
            >
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span className="fw-semi-bold">{endpoint.title}</span>
                <SubtleBadge pill bg={endpoint.regionBg}>
                  {endpoint.region}
                </SubtleBadge>
              </div>
              <p className="fs--1 text-600 mb-3">{endpoint.hint}</p>
              <Form.Label className="fw-semi-bold">آدرس سرور</Form.Label>
              <InputGroup className="mb-0">
                <Form.Control
                  readOnly
                  value={endpoint.url}
                  dir="ltr"
                  className="font-monospace fs--1"
                  style={{ textAlign: 'left' }}
                />
                <Button
                  variant="outline-primary"
                  onClick={() => void copy(urlKey, endpoint.url)}
                >
                  <FontAwesomeIcon icon="copy" className="me-1" />
                  {copied === urlKey ? 'کپی شد' : 'کپی'}
                </Button>
              </InputGroup>
              {endpoint.showConfig && (
                <div className="mt-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Label className="fw-semi-bold mb-0">
                      نمونه mcp.json
                    </Form.Label>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 fs--2 text-600"
                      onClick={() => void copy(jsonKey, json)}
                    >
                      <FontAwesomeIcon icon="copy" className="me-1" />
                      {copied === jsonKey ? 'کپی شد' : 'کپی'}
                    </Button>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={8}
                    readOnly
                    value={json}
                    dir="ltr"
                    className="font-monospace fs--1"
                    style={{ textAlign: 'left', direction: 'ltr' }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </Card.Body>
    </>
  );

  return embedded ? content : <Card>{content}</Card>;
}
